import { ChevronDown, ChevronUp, Heart, UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { US_STATES } from '@/constants/territories/states';
import { TERRITORIES_TOUR_CLASSES } from '@/constants/tour/classNames';
import { getTerritoryColor } from '@/lib/territories';
import { cn } from '@/lib/ui/utils';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

import { TerritoryDetailView } from './TerritoryDetailView';
import { TerritoryStatusBadge } from './TerritoryStatusBadge';

interface TerritoryItemProps {
  territory: Territory;
  isEditTab?: boolean;
  isOwnedByUser?: boolean;
  onTerritoryClick?: (territory: Territory) => void;
}

const TerritoryItem = ({
  territory,
  isEditTab = false,
  isOwnedByUser = false,
  onTerritoryClick,
}: TerritoryItemProps) => {
  const { setFocusedTerritory, focusedTerritory } = useTerritoryStore();

  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isZipCodesPopoverOpen, setIsZipCodesPopoverOpen] = useState(false);
  const { zorTerritorySettings } = useZorTerritorySettingsStore();
  const soldTerritory = territory.status == 'SOLD';

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (focusedTerritory && location.pathname == '/dashboard/territories') {
      setIsExpanded(territory.id === focusedTerritory.id);
    }
  }, [focusedTerritory]);

  const handleTerritoryClick = (territory: Territory) => {
    if (onTerritoryClick) {
      onTerritoryClick(territory);
    } else {
      setFocusedTerritory(territory);
    }

    if (location.pathname !== '/dashboard/territories') {
      navigate('/dashboard/territories', {
        state: { selectedTerritory: true },
      });
    }
  };

  const maxFirstLineZips = 5;
  const firstLineZips = territory.zcta_codes.slice(0, maxFirstLineZips);
  const hasMoreZips = territory.zcta_codes.length > maxFirstLineZips;

  return (
    <>
      <div
        className={cn(
          TERRITORIES_TOUR_CLASSES.TERRITORY_ITEM_ROW,
          'flex items-center justify-between gap-6 border-t border-border px-6 py-3 first:border-t-0 max-md:flex-col max-md:items-start max-md:gap-3 max-md:p-3'
        )}
      >
        <div className="flex flex-col gap-2 truncate lg:flex-row lg:items-center">
          <div
            className="as-strong flex cursor-pointer items-center gap-2 border-black text-gray-700 hover:border-b-2"
            onClick={() => handleTerritoryClick(territory)}
          >
            <p>#{territory.id.toString().padStart(5, '0')}</p>
            <p>|</p>
            <p>
              {territory.name ||
                `Territory ${territory.id.toString().padStart(5, '0')}`}
            </p>
          </div>
          <p className="as-body-02 as-strong text-gray-700">|</p>
          <div className="flex items-center gap-1">
            <p className="truncate text-gray-700 max-lg:text-xs">
              Zips: {firstLineZips.join(', ')}
              {hasMoreZips && '...'}
            </p>
            {hasMoreZips && (
              <Popover
                open={isZipCodesPopoverOpen}
                onOpenChange={setIsZipCodesPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <button className="flex items-center justify-center rounded p-0.5 hover:bg-gray-100">
                    <ChevronDown className="h-3 w-3 text-gray-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4">
                  <div className="space-y-2">
                    <h4 className="as-body-02 as-strong text-gray-700">
                      All Zip Codes
                    </h4>
                    <p className="as-body-03 break-words text-gray-600">
                      {territory.zcta_codes.join(', ')}
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <p className="text-muted-foreground max-lg:hidden">|</p>
          <p className="text-gray-700 max-lg:text-xs">
            {US_STATES[territory.state as keyof typeof US_STATES]}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 max-md:w-full">
          <div className="flex flex-wrap items-center gap-2">
            {((territory.likes_count > 0 && !territory.is_liked) ||
              (territory.likes_count > 1 && territory.is_liked)) &&
              !soldTerritory && (
                <div className="flex items-center gap-2 rounded-full border border-border px-4 py-2">
                  {territory.territory_like_photos
                    ?.slice(0, 3)
                    .map((item, index) => (
                      <Avatar
                        key={index}
                        className={cn('h-6 w-6', index !== 0 && 'ml-[-20px]')}
                      >
                        <AvatarImage src={item} />
                        <AvatarFallback>
                          <UserIcon className="h-3 w-3" />
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  <p className="as-strong as-title-03 max-lg:text-xs">
                    {`${
                      territory.likes_count > 2
                        ? `${territory.likes_count - (territory.is_liked ? 1 : 0)} others have liked this Territory`
                        : '1 other has liked this Territory'
                    }`}
                  </p>
                </div>
              )}
            {isOwnedByUser && territory.status != 'SOLD' && (
              <Button
                variant="outline"
                disabled={false}
                className="max-lg:h-8 max-lg:text-xs"
              >
                {territory.status == 'PENDING'
                  ? 'Requested by You'
                  : 'Reserved by You'}
              </Button>
            )}
            {territory.is_liked && !soldTerritory && (
              <Button
                variant="outline"
                disabled={false}
                className="max-lg:h-8 max-lg:text-xs"
              >
                <Heart
                  fill={'currentColor'}
                  className="size-4 text-red-500"
                  stroke="black"
                />
                Liked
              </Button>
            )}
            <TerritoryStatusBadge
              label={territory.status}
              color={getTerritoryColor(territory, zorTerritorySettings)}
            />
          </div>
          {!isEditTab && !soldTerritory && (
            <Button
              size="icon"
              variant="outline"
              onClick={handleToggleExpanded}
              className={TERRITORIES_TOUR_CLASSES.TERRITORY_EXPAND_BUTTON}
            >
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          )}
        </div>
      </div>
      {isExpanded && !isEditTab && (
        <TerritoryDetailView territory={territory} />
      )}
    </>
  );
};

export { TerritoryItem };
